/**
 * LegacyRedirect — SPA-side mirror of /app/backend/legacy_redirects.py
 *
 * Why this exists: the Kubernetes ingress on the Emergent preview / deploy
 * routes ALL non-`/api/*` requests to the React SPA, never to the FastAPI
 * backend.  The backend middleware that returns 410/301 for spam paths
 * therefore never sees these requests in production.
 *
 * This SPA guard catches the same paths and sends the strongest signals an
 * SPA can send to Google: `<meta name="robots" content="noindex,nofollow,
 * noarchive">`, an immediate `window.location.replace('/')`, and a React
 * `<Navigate replace>` fallback.  HTTP status remains 200 (an SPA cannot
 * issue a true 301/410), but combined with noindex this still gets the
 * legacy spam pages deindexed by Google + Bing.
 */
import { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const EXPLICIT_REDIRECTS = {
  "/play-with-bitcoin-what-to-know": "/",
  "/tips-for-managing-your-bankroll-at-casino-online-canada-3": "/",
  "/usdt-bonus-overview-and-options-for-dutch-players": "/",
  "/casino-online-beveiligingsgids-voor-belgische-spelers": "/",
  "/online-casino-account-verifizierung-in-luxemburg-schritt-fur-schritt-guide": "/",
  "/online-wetten-in-luxemburg-zahlungsarten-im-uberblick": "/",
  "/blackchristianpeoplemeet-review": "/",
};

const SPAM_PATH_PREFIXES = [
  "/play-with-",
  "/casino-",
  "/online-casino-",
  "/online-wetten",
  "/usdt-",
  "/bitcoin-",
  "/tips-for-managing",
  "/blackchristian",
  "/bonus-",
  "/wetten-",
  "/zahlung-",
  "/bookmaker",
  "/sportsbook",
  "/poker-",
  "/slots-",
];

const FUZZY_RULES = [
  [/^\/(clinton|clintonmo|clinton-mo|clinton-liquor)\/?$/i, "/locations/clinton-mo"],
  [/^\/(warsaw|warsawmo|warsaw-mo)\/?$/i, "/locations/warsaw-mo"],
  [/^\/(sedalia|sedaliamo|sedalia-mo)\/?$/i, "/locations/sedalia-mo"],
  [/^\/(truman|truman-?lake|trumanlake-mo)\/?$/i, "/locations/truman-lake"],
  [/^\/(bourbon|whiskey|whisky)\/?$/i, "/spirits/category/bourbon"],
  [/^\/(wine|wines)\/?$/i, "/spirits/category/wine"],
  [/^\/(beer|craft-?beer)\/?$/i, "/spirits/category/beer"],
  [/^\/(tequila|mezcal)\/?$/i, "/spirits/category/tequila"],
  [/^\/(vodka)\/?$/i, "/spirits/category/vodka"],
  [/^\/(gin)\/?$/i, "/spirits/category/gin"],
  [/^\/(rum)\/?$/i, "/spirits/category/rum"],
  [/^\/(spirits|liquor|liquors)\/?$/i, "/spirits"],
  [/^\/(shop|store|buy|online-?shop)\/?$/i, "/spirits"],
  [/^\/(catalog|products?)\/?$/i, "/spirits"],
  [/^\/(map|directions|location|find-us)\/?$/i, "/visit"],
  [/^\/(hours|opening-?hours)\/?$/i, "/visit"],
  [/^\/(blog|posts|news|journal)\/?$/i, "/blog"],
  [/^\/(recipes|cocktails)\/?$/i, "/recipes"],
  [/^\/(about-?us|story|our-?story)\/?$/i, "/about"],
  [/^\/(contact-?us|contact)\/?$/i, "/contact"],
  [/^\/(faq|faqs|questions)\/?$/i, "/faq"],
  [/^\/(reviews|testimonials)\/?$/i, "/reviews"],
];

// Normalize: strip trailing slash (except root) for matching.
function _normalize(path) {
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

/**
 * resolveLegacyPath(path) → string target, or null if the path is legitimate.
 * Pure function so it's unit-testable.
 */
export function resolveLegacyPath(pathname) {
  const path = _normalize(pathname);

  if (path in EXPLICIT_REDIRECTS) return EXPLICIT_REDIRECTS[path];

  const lower = path.toLowerCase();
  if (SPAM_PATH_PREFIXES.some((p) => lower.startsWith(p))) return "/";

  for (const [re, target] of FUZZY_RULES) {
    if (re.test(path)) return target;
  }

  return null;
}

/**
 * Route element that, if the current URL is a known legacy/spam path,
 * sets noindex + redirects.  Otherwise falls through to the `fallback`
 * element (typically <NotFound />).
 */
export default function LegacyRedirect({ fallback }) {
  const { pathname } = useLocation();
  const target = resolveLegacyPath(pathname);

  // Hard-redirect via window.location for non-JS crawlers that follow it.
  useEffect(() => {
    if (target && target !== pathname) {
      // Replace so the spam URL doesn't pollute browser history.
      window.location.replace(target);
    }
  }, [target, pathname]);

  if (!target) return fallback;

  return (
    <>
      <Helmet>
        <title>Page moved · Liquor Studio</title>
        <meta name="robots" content="noindex,nofollow,noarchive,nosnippet" />
        <meta name="googlebot" content="noindex,nofollow" />
        <meta httpEquiv="refresh" content={`0; url=${target}`} />
        <link rel="canonical" href={`https://liquor.studio${target}`} />
      </Helmet>
      <Navigate to={target} replace />
    </>
  );
}
